import { apiClient } from './request'

type JsonObject = Record<string, unknown>

const text = (value: unknown) => (typeof value === 'string' ? value : '')
const num = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(value ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}
const strings = (value: unknown) =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []

/** trace 列表项 */
export interface Trace {
  id: string
  /** RFC3339 时间 */
  timestamp: string
  name: string
  environment: string
  tags: string[]
  /** 输入 */
  input?: unknown
  /** 输出 */
  output?: unknown
  userId: string
  sessionId: string
  latencyMs: number
  level: string
  statusMessage: string
}

export interface ListTracesParams {
  tags: string[]
  pageSize: number
  /** RFC3339 */
  fromTimestamp?: string
  /** RFC3339 */
  toTimestamp?: string
  /** 只看失败 */
  onlyFailed?: boolean
  /** Trace ID，精确匹配 */
  traceId?: string
  /** 会话 ID，精确匹配 */
  sessionId?: string
  /** 用户名，精确匹配 */
  userName?: string
  /** 分页游标，为空表示第一页 */
  cursor?: string
  /** 是否返回近似总数 */
  withTotal?: boolean
}

export interface ListTracesResult {
  items: Trace[]
  nextCursor: string
  hasMore: boolean
  /** 仅 withTotal=true 时有值 */
  total?: number
}

/** trace 详情，字段名与接口一致，latency 单位毫秒 */
export interface TraceDetails {
  id: string
  timestamp?: string
  name?: string
  input?: unknown
  output?: unknown
  session_id?: string
  user_id?: string
  environment?: string
  tags: string[]
  metadata?: unknown
  latency?: number
  total_cost?: number
  level?: string
  status_message?: string
  observation_count?: number
  input_tokens?: string | number
  output_tokens?: string | number
  total_tokens?: string | number
  truncated?: boolean
  [key: string]: unknown
}

function trace(raw: JsonObject): Trace {
  return {
    id: text(raw.id),
    timestamp: text(raw.timestamp),
    name: text(raw.name),
    environment: text(raw.environment),
    tags: strings(raw.tags),
    input: raw.input,
    output: raw.output,
    userId: text(raw.user_id),
    sessionId: text(raw.session_id),
    latencyMs: num(raw.latency_ms),
    level: text(raw.level),
    statusMessage: text(raw.status_message)
  }
}

async function data(path: string, body: JsonObject): Promise<JsonObject> {
  const envelope = (await apiClient.post<JsonObject>('/v2/' + path, body)).data
  return (envelope.data ?? {}) as JsonObject
}

export const langfuseApi = {
  listTraces: async (params: ListTracesParams): Promise<ListTracesResult> => {
    const raw = await data('ListTraces', {
      tags: params.tags,
      page_size: params.pageSize,
      cursor: params.cursor || undefined,
      with_total: params.withTotal || undefined,
      from_timestamp: params.fromTimestamp || undefined,
      to_timestamp: params.toTimestamp || undefined,
      only_failed: params.onlyFailed || undefined,
      trace_id: params.traceId || undefined,
      session_id: params.sessionId || undefined,
      user_name: params.userName || undefined
    })
    return {
      items: ((raw.items ?? []) as JsonObject[]).map(trace),
      nextCursor: text(raw.next_cursor),
      hasMore: Boolean(raw.has_more),
      // 仅后端计算了总数时返回
      total: raw.total_approximate ? num(raw.total) : undefined
    }
  },

  getTraceDetails: async (traceId: string): Promise<TraceDetails> => {
    const raw = await data('GetTraceDetails', { trace_id: traceId })
    return { ...raw, id: text(raw.id), tags: strings(raw.tags) } as TraceDetails
  }
}
